function decodeAttr(value: string) {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function escapeHtmlAttr(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function sameSrc(left: string, right: string) {
  if (left === right) {
    return true;
  }

  try {
    return decodeURI(left) === decodeURI(right);
  } catch {
    return false;
  }
}

function readAttrs(source: string) {
  const attrs: Array<{ name: string; value: string }> = [];
  const pattern = /([:@\w-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let match = pattern.exec(source);

  while (match) {
    attrs.push({
      name: match[1].toLowerCase(),
      value: decodeAttr(match[2] ?? match[3] ?? match[4] ?? ""),
    });
    match = pattern.exec(source);
  }

  return attrs;
}

function imageTagMatches(tag: string, src: string, alt: string | undefined) {
  const attrs = readAttrs(tag.replace(/^<img\b/i, "").replace(/\/?>$/, ""));
  const tagSrc = attrs.find(attr => attr.name === "src")?.value;
  const tagAlt = attrs.find(attr => attr.name === "alt")?.value ?? "";

  if (!tagSrc || !sameSrc(tagSrc, src)) {
    return false;
  }

  return (alt ?? "") === tagAlt;
}

function markdownImage(alt: string, src: string, title?: string) {
  const safeAlt = alt.replace(/[[\]]/g, "");
  if (title) {
    return `![${safeAlt}](${src} "${title.replace(/"/g, "")}")`;
  }

  return `![${safeAlt}](${src})`;
}

function htmlImage(alt: string, src: string, width: number, title?: string) {
  const titleAttr = title ? ` title="${escapeHtmlAttr(title)}"` : "";
  return `<img alt="${escapeHtmlAttr(alt)}" src="${escapeHtmlAttr(src)}"${titleAttr} width="${width}" />`;
}

function destinationOf(destination: string) {
  if (destination.startsWith("<") && destination.endsWith(">")) {
    return destination.slice(1, -1);
  }

  return destination;
}

function titleOf(quoted?: string) {
  if (!quoted) {
    return undefined;
  }

  return quoted.slice(1, -1).replace(/\\"/g, '"').replace(/\\'/g, "'");
}

export function setMarkdownImageWidth(markdown: string, src: string, alt: string | undefined, width: number | null) {
  const withHtml = markdown.replace(/<img\b[^>]*>/gi, tag => {
    if (!imageTagMatches(tag, src, alt)) {
      return tag;
    }

    const attrs = readAttrs(tag.replace(/^<img\b/i, "").replace(/\/?>$/, ""));
    const tagAlt = attrs.find(attr => attr.name === "alt")?.value ?? "";
    const tagSrc = attrs.find(attr => attr.name === "src")?.value ?? src;
    const title = attrs.find(attr => attr.name === "title")?.value;

    if (width === null) {
      return markdownImage(tagAlt, tagSrc, title);
    }

    return htmlImage(tagAlt, tagSrc, width, title);
  });

  if (width === null) {
    return withHtml;
  }

  return withHtml.replace(
    /!\[([^\]]*)\]\(\s*(<[^>\s]+>|[^)\s]+)(?:\s+("(?:\\.|[^"])*"|'(?:\\.|[^'])*'))?\s*\)/g,
    (full, imageAlt: string, destination: string, quotedTitle?: string) => {
    const imageSrc = destinationOf(destination);
    if (!sameSrc(imageSrc, src) || (alt ?? "") !== imageAlt) {
      return full;
    }

    return htmlImage(imageAlt, imageSrc, width, titleOf(quotedTitle));
    },
  );
}

interface IImageMatch {
  start: number;
  end: number;
  text: string;
}

function findMarkdownImage(markdown: string, src: string, alt: string | undefined): IImageMatch | null {
  const htmlPattern = /<img\b[^>]*>/gi;
  let htmlMatch = htmlPattern.exec(markdown);

  while (htmlMatch) {
    if (imageTagMatches(htmlMatch[0], src, alt)) {
      return { start: htmlMatch.index, end: htmlMatch.index + htmlMatch[0].length, text: htmlMatch[0] };
    }
    htmlMatch = htmlPattern.exec(markdown);
  }

  const markdownPattern = /!\[([^\]]*)\]\(\s*(<[^>\s]+>|[^)\s]+)(?:\s+("(?:\\.|[^"])*"|'(?:\\.|[^'])*'))?\s*\)/g;
  let markdownMatch = markdownPattern.exec(markdown);

  while (markdownMatch) {
    const imageSrc = destinationOf(markdownMatch[2]);
    if (sameSrc(imageSrc, src) && (alt ?? "") === markdownMatch[1]) {
      return { start: markdownMatch.index, end: markdownMatch.index + markdownMatch[0].length, text: markdownMatch[0] };
    }
    markdownMatch = markdownPattern.exec(markdown);
  }

  return null;
}

function removalRange(markdown: string, start: number, end: number) {
  const lineStart = markdown.lastIndexOf("\n", start - 1) + 1;
  const nextNewline = markdown.indexOf("\n", end);
  const lineEnd = nextNewline === -1 ? markdown.length : nextNewline;
  const aloneOnLine = markdown.slice(lineStart, start).trim() === "" && markdown.slice(end, lineEnd).trim() === "";

  if (!aloneOnLine) {
    const leftIsSpace = markdown[start - 1] === " ";
    const rightIsSpace = markdown[end] === " ";
    return { from: start, to: rightIsSpace && leftIsSpace ? end + 1 : end };
  }

  let removeFrom = lineStart;
  const removeTo = nextNewline === -1 ? markdown.length : nextNewline + 1;
  if (nextNewline === -1 && lineStart > 0) {
    removeFrom = lineStart - 1;
  }

  const next = markdown.slice(0, removeFrom) + markdown.slice(removeTo);
  if (removeFrom === 0 && next.startsWith("\n")) {
    return { from: 0, to: Math.min(markdown.length, removeTo + 1) };
  }

  if (removeFrom >= 2 && next[removeFrom - 2] === "\n" && next[removeFrom - 1] === "\n" && next[removeFrom] === "\n") {
    return { from: removeFrom, to: Math.min(markdown.length, removeTo + 1) };
  }

  return { from: removeFrom, to: removeTo };
}

function removeSpan(markdown: string, start: number, end: number) {
  const range = removalRange(markdown, start, end);
  return markdown.slice(0, range.from) + markdown.slice(range.to);
}

function insertImageBlock(markdown: string, index: number, imageText: string) {
  const before = markdown.slice(0, index);
  const after = markdown.slice(index);
  let prefix = "";
  let suffix = "";

  if (before.length > 0 && !before.endsWith("\n\n")) {
    prefix = before.endsWith("\n") ? "\n" : "\n\n";
  }

  if (after.length > 0 && !after.startsWith("\n\n")) {
    suffix = after.startsWith("\n") ? "\n" : "\n\n";
  }

  return `${before}${prefix}${imageText}${suffix}${after}`;
}

export function removeMarkdownImage(markdown: string, src: string, alt: string | undefined) {
  const found = findMarkdownImage(markdown, src, alt);
  if (!found) {
    return markdown;
  }

  return removeSpan(markdown, found.start, found.end);
}

export function moveMarkdownImage(markdown: string, src: string, alt: string | undefined, insertAt: number) {
  const found = findMarkdownImage(markdown, src, alt);
  if (!found) {
    return markdown;
  }

  const range = removalRange(markdown, found.start, found.end);
  if (insertAt >= range.from && insertAt <= range.to) {
    return markdown;
  }

  const without = markdown.slice(0, range.from) + markdown.slice(range.to);
  let index = insertAt;
  if (insertAt >= range.to) {
    index = insertAt - (range.to - range.from);
  } else if (insertAt > range.from) {
    index = range.from;
  }

  index = Math.max(0, Math.min(index, without.length));
  return insertImageBlock(without, index, found.text);
}
